import { StyleProvider } from '@ant-design/cssinjs';
import { App as AntApp, ConfigProvider, theme as antdTheme } from 'antd';
import { Spinner } from '@/components/Loader';
import enGB from 'antd/locale/en_GB';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { FONT, NAV, palette, themeConfig, type ThemeMode } from './tokens';

const STORAGE_KEY = 'tlp.pharmacy.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * What the user *chose*, which is not the same thing as what is painted.
 *
 * `system` is the default, and the reason this type has to exist: a counter
 * tablet that flips to dark at sunset should take the till with it, and a
 * two-way toggle can only answer that by making somebody come back and flip it
 * by hand - during a shift, on the one screen they cannot afford to go hunting
 * through. Everything downstream keeps taking the resolved `ThemeMode`, so
 * `system` never leaks past this file.
 */
export type ThemePreference = ThemeMode | 'system';

type ThemeContextValue = {
  /** What is actually painted. `system` is already resolved here. */
  mode: ThemeMode;
  /** What the user picked. Render the control from this, never from `mode`. */
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** Light ↔ dark, for a plain two-way control. Steps out of `system`. */
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemMode(): ThemeMode {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/*
 * Storage can throw outright - private browsing, a locked-down profile - and a
 * colour preference is not worth a blank till, so both directions swallow. A
 * stored 'light' or 'dark' from before this grew a third option still reads
 * back as itself, so nobody's existing choice is lost.
 */
function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* No storage. Fall through and follow the device. */
  }
  return 'system';
}

function writePreference(preference: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* The preference holds for this session and is forgotten on the next. */
  }
}

/**
 * Publishes the resolved antd tokens as the CSS variables `styles.css` maps
 * into Tailwind. It sits inside ConfigProvider because `useToken` has to read
 * the config above it - which is why this is a component and not a hook call in
 * ThemeProvider itself.
 *
 * Colours come from the resolved token so Tailwind cannot disagree with what
 * antd actually painted; the ring and the shadows come straight from the
 * palette, because antd has no token for either.
 */
function TailwindTokenBridge({ mode, children }: { mode: ThemeMode; children: ReactNode }) {
  const { token } = antdTheme.useToken();

  useEffect(() => {
    const p = palette(mode);
    const root = document.documentElement;
    const vars: Record<string, string> = {
      '--tlp-brand': token.colorPrimary,
      /*
       * The accent as INK. Identical to `--tlp-brand` in light mode and
       * deliberately different in dark, where the fill green measures 2.50:1 as
       * text on the card and sage is used instead. Anything that writes the
       * accent as TEXT must read this one.
       */
      '--tlp-brand-ink': p.accentText,
      /* The sider is near-black in both themes, so its roles cannot be derived
         from the page palette. */
      '--tlp-nav-bg': p.navBg,
      '--tlp-nav-ink': p.navFg,
      '--tlp-nav-ink-muted': p.navFgMuted,
      '--tlp-nav-selected-bg': p.navSelBg,
      '--tlp-nav-selected-ink': p.navSelFg,
      '--tlp-nav-marker': p.navMarker,
      /* Rules on the sider, tuned per theme for the sider's own ground. */
      '--tlp-nav-divider': p.navDivider,
      '--tlp-surface': token.colorBgLayout,
      '--tlp-surface-raised': token.colorBgContainer,
      '--tlp-elevated': token.colorBgElevated,
      '--tlp-ink': token.colorText,
      '--tlp-ink-muted': token.colorTextSecondary,
      '--tlp-ink-subtle': token.colorTextTertiary,
      '--tlp-ink-placeholder': token.colorTextPlaceholder,
      '--tlp-hairline': token.colorBorderSecondary,
      /* The control edge, for the hand-built inputs in the keyed grids. */
      '--tlp-hairline-strong': p.borderStrong,
      /* The neutral hover/selected fill - shadcn's `--accent`. */
      '--tlp-muted': token.colorFillTertiary,
      /* One step heavier than muted. The current nav row sits on this. */
      '--tlp-muted-strong': token.colorFillSecondary,
      '--tlp-positive': token.colorSuccess,
      '--tlp-caution': token.colorWarning,
      '--tlp-danger': token.colorError,
      /* The destructive hover pair. antd has no token for either. */
      '--tlp-danger-soft': p.dangerSoft,
      '--tlp-danger-strong': p.dangerStrong,
      '--tlp-font-body': FONT.body,
      '--tlp-font-display': FONT.display,
      '--tlp-font-mono': FONT.mono,
      /* The sidebar's rail, so CSS and the layout cannot drift apart. */
      '--nav-gutter': `${NAV.gutter}px`,
      '--nav-rail': `${NAV.railInset}px`,
      '--tlp-radius': `${token.borderRadius}px`,
      '--tlp-radius-lg': `${token.borderRadiusLG}px`,
      '--tlp-ring': p.ring,
      '--tlp-shadow-sm': p.shadowSm,
      '--tlp-shadow-md': p.shadowMd,
      '--tlp-btn-primary-paint': p.btnPrimaryPaint,
      '--tlp-btn-primary-edge': p.btnPrimaryEdge,
      '--tlp-btn-primary-ink-hover': p.btnPrimaryInkHover,
      '--tlp-btn-primary-ink-active': p.btnPrimaryInkActive,
      '--tlp-btn-default-edge': p.btnDefaultEdge,
      '--tlp-btn-danger-edge': p.btnDangerEdge,
      '--tlp-btn-disabled-bg': p.btnDisabledBg,
      '--tlp-btn-disabled-ink': p.btnDisabledInk,
      '--tlp-btn-primary-disabled-bg': p.btnPrimaryDisabledBg,
      '--tlp-btn-primary-disabled-ink': p.btnPrimaryDisabledInk,
      '--tlp-card-shell': p.cardShell,
      '--tlp-card-shell-edge': p.cardShellEdge,
      '--tlp-card-edge': p.cardEdge,
      '--tlp-card-tray': p.cardTray,
      '--tlp-popup-shell': p.popupShell,
      '--tlp-popup-card': p.popupCard,
    };
    for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value);
  }, [token, mode]);

  return children;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [system, setSystem] = useState<ThemeMode>(systemMode);

  /*
   * Kept live rather than read once at boot, because reading once is the exact
   * failure this preference exists to prevent: the tablet goes dark at sunset
   * and the till that has been open since the morning shift stays white.
   */
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const sync = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    media.addEventListener('change', sync);
    // The device may have moved between the first read and this subscription.
    setSystem(media.matches ? 'dark' : 'light');
    return () => media.removeEventListener('change', sync);
  }, []);

  const mode: ThemeMode = preference === 'system' ? system : preference;

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writePreference(next);
  }, []);

  useEffect(() => {
    // Lets native widgets (scrollbars, date inputs, autofill) match the theme.
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      preference,
      setPreference,
      toggle: () => setPreference(mode === 'dark' ? 'light' : 'dark'),
    }),
    [mode, preference, setPreference],
  );

  return (
    <ThemeContext value={value}>
      {/* `layer` puts antd's runtime CSS in @layer antd so Tailwind wins ties. */}
      <StyleProvider layer>
        {/*
          Antd's own waits - a table refreshing, a select fetching - use the
          app's ring rather than antd's dots, so one kind of wait looks the
          same everywhere. See components/Loader.
        */}
        <ConfigProvider
          theme={themeConfig(mode)}
          locale={enGB}
          componentSize="middle"
          spin={{ indicator: <Spinner size={22} /> }}
        >
          <TailwindTokenBridge mode={mode}>
            {/* Supplies the message/modal/notification instances that read theme context. */}
            <AntApp className="h-full">{children}</AntApp>
          </TailwindTokenBridge>
        </ConfigProvider>
      </StyleProvider>
    </ThemeContext>
  );
}

export function useThemeMode(): ThemeContextValue {
  const context = use(ThemeContext);
  if (!context) throw new Error('useThemeMode must be used inside <ThemeProvider>');
  return context;
}
